import type { Attachment, FileDto, ProblemDetails } from '@agent-platform/shared';
import { ApiError, mutationHeaders } from '@/lib/api';

/** Where a stored file can be viewed (same-origin, cookie-authenticated). */
export const fileContentUrl = (fileId: string) => `/api/admin/files/${fileId}/content`;

export const toAttachment = (f: FileDto): Attachment => ({
  fileId: f.id,
  name: f.name,
  mediaType: f.mediaType,
  sizeBytes: f.sizeBytes,
});

const EXTENSION_TYPES: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
  txt: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  pdf: 'application/pdf',
};

/**
 * Uploads one file (the request body is the file itself) and reports progress (0–1). The server
 * checks the bytes, so a wrong or missing browser type is fine. `path` is the admin endpoint that
 * takes the upload (`/files` by default); it answers 201 with the created resource.
 */
export function uploadFile<T = FileDto>(
  file: File,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
  path = '/files',
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/admin${path}?name=${encodeURIComponent(file.name || 'file')}`);
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    xhr.setRequestHeader(
      'content-type',
      file.type || EXTENSION_TYPES[ext] || 'application/octet-stream',
    );
    xhr.setRequestHeader('accept', 'application/json');
    for (const [k, v] of Object.entries(mutationHeaders())) xhr.setRequestHeader(k, v);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // Not JSON (e.g. a proxy error page).
      }
      if (xhr.status === 201) resolve(body as T);
      else reject(new ApiError(xhr.status, body as Partial<ProblemDetails> | null));
    };
    xhr.onerror = () =>
      reject(new ApiError(0, { detail: 'Cannot reach the server. Check your connection.' }));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}
